import React from "react";
import { motion } from "framer-motion";
import { Btn } from "./ui";
import { useContact } from "../../context/ContactContext";

const stack = [
  { n: "01", title: "Hook & Pacing", desc: "Keep attention from the first second." },
  { n: "02", title: "Motion & Visual Storytelling", desc: "Make the message easier to understand." },
  { n: "03", title: "Sound Design", desc: "Make every cut feel intentional." },
  { n: "04", title: "Platform-Ready Delivery", desc: "Built for Shorts, Reels & YouTube." },
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.12 },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="line-mask">
    <motion.span className="block" variants={line} custom={i} initial="hidden" animate="show">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { openTally } = useContact();

  return (
    <section id="top" className="relative pt-32 md:pt-44 pb-16 md:pb-24 px-6 md:px-10">
      <div className="mx-auto max-w-7xl grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-8 inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase"
            style={{ color: "var(--text-secondary)" }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--accent-color)" }} />
            Video editing studio
          </motion.div>

          <h1 className="font-heading font-semibold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
            <MaskLine i={0}>Tired of boring</MaskLine>
            <MaskLine i={1}>content nobody</MaskLine>
            <MaskLine i={2}>
              <span style={{ color: "var(--accent-color)" }}>watches?</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 max-w-md text-base md:text-lg leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            We turn raw footage into content that gets attention.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Btn href="#work" variant="solid" data-testid="hero-view-work">
              View Work
            </Btn>
            <Btn as="button" onClick={openTally} data-testid="hero-start-project">
              Start a Project
            </Btn>
          </motion.div>
        </div>

        {/* Right — The Editing Stack card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div
            className="group relative w-full overflow-hidden rounded-2xl border p-7 md:p-9 flex flex-col justify-between transition-colors duration-500 hover:border-[color:var(--accent-color)]"
            style={{
              borderColor: "var(--border-color)",
              background: "rgba(15,15,15,0.55)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              aspectRatio: "4/5",
            }}
            data-testid="hero-editing-stack"
          >
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-[90px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              style={{ background: "var(--accent-color)", opacity: 0.1 }}
            />

            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--accent-color)" }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--accent-color)" }} />
                The Editing Stack
              </div>

              <ul className="mt-7 md:mt-8 space-y-5 md:space-y-6">
                {stack.map((s, i) => (
                  <motion.li
                    key={s.n}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="flex gap-4"
                  >
                    <span className="font-heading text-sm font-medium tabular-nums pt-0.5" style={{ color: "var(--accent-color)" }}>
                      {s.n}
                    </span>
                    <div>
                      <p className="font-heading text-base md:text-lg font-medium tracking-tight leading-tight">{s.title}</p>
                      <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--border-color)" }}>
              <p className="text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--text-secondary)" }}>Built for</p>
              <p className="mt-1.5 font-heading text-sm md:text-base font-medium tracking-tight">
                Founders <span style={{ color: "var(--accent-color)" }}>·</span> Coaches <span style={{ color: "var(--accent-color)" }}>·</span> Creators
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
